import { useMemo } from 'react';
import { capabilityReport, type Capability, type SubtitleDocument } from '../../core';
import { targetName, type TargetId } from '../session/exportDownload';

/**
 * Capability-loss UI shared by the Converter and the Studio in-place Export
 * (cue-forge-zxw.10) — both read the same core `capabilityReport`, so the loss
 * verdict is identical everywhere and lives in one place. Styling classes
 * (`cf-lossmatrix`/`cf-matrix`/`cf-warnings`) are the global rules from
 * Converter.css, which the app bundle always loads.
 */

/** Human label per capability, in the order columns/lists should appear. */
const CAPABILITY_LABELS: ReadonlyArray<[Capability, string]> = [
  ['italic', 'Italic'],
  ['bold', 'Bold'],
  ['underline', 'Underline'],
  ['color', 'Color'],
  ['voice', 'Voice'],
  ['position', 'Position'],
  ['override', 'Other tags'],
];

function labelsFor(caps: Capability[]): string {
  const set = new Set(caps);
  const names = CAPABILITY_LABELS.filter(([c]) => set.has(c)).map(([, label]) => label);
  return names.length > 0 ? names.join(', ') : 'none';
}

/**
 * The compact Kept/Lost line — the summary half of {@link LossMatrix}, used
 * inline by the Studio export where the full comparison table would be too heavy.
 */
export function LossSummary({
  preserved,
  dropped,
}: {
  preserved: Capability[];
  dropped: Capability[];
}) {
  return (
    <>
      <span className="cf-lossmatrix__keep">Kept: {labelsFor(preserved)}</span>
      {' · '}
      <span className="cf-lossmatrix__lose">Lost: {labelsFor(dropped)}</span>
    </>
  );
}

/**
 * Proactive loss matrix (H1, free): before converting, show exactly what the
 * selected target preserves vs drops, plus an expandable table comparing every
 * target. Turns post-hoc capability warnings into pre-conversion transparency.
 */
export function LossMatrix({ doc, selected }: { doc: SubtitleDocument; selected: TargetId }) {
  const report = useMemo(() => capabilityReport(doc), [doc]);
  const used = useMemo(() => {
    const set = new Set<Capability>(report.flatMap((e) => [...e.preserved, ...e.dropped]));
    return CAPABILITY_LABELS.filter(([c]) => set.has(c));
  }, [report]);

  if (used.length === 0) {
    return (
      <p className="cf-lossmatrix__none">
        This file uses no styling or positioning — every format is a perfect match.
      </p>
    );
  }

  const current = report.find((e) => e.target === selected)!;
  const summary =
    current.dropped.length === 0
      ? 'fully preserved'
      : `${current.dropped.length} feature${current.dropped.length === 1 ? '' : 's'} dropped`;

  return (
    <div className="cf-lossmatrix">
      <div className="cf-lossmatrix__current" aria-live="polite">
        <h2 className="cf-lossmatrix__heading">
          {targetName(selected)}: {summary}
        </h2>
        <p className="cf-lossmatrix__keep">Kept: {labelsFor(current.preserved)}</p>
        <p className="cf-lossmatrix__lose">Lost: {labelsFor(current.dropped)}</p>
      </div>

      <details className="cf-lossmatrix__full">
        <summary>Compare all formats</summary>
        <table className="cf-matrix">
          <caption className="cf-visually-hidden">
            Styling and positioning each target format preserves
          </caption>
          <thead>
            <tr>
              <th scope="col">Format</th>
              {used.map(([cap, label]) => (
                <th key={cap} scope="col">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.map((entry) => {
              const kept = new Set(entry.preserved);
              return (
                <tr key={entry.target}>
                  <th scope="row">{targetName(entry.target)}</th>
                  {used.map(([cap]) => (
                    <td key={cap} className={kept.has(cap) ? 'cf-matrix__keep' : 'cf-matrix__lose'}>
                      <span aria-hidden="true">{kept.has(cap) ? '✓' : '✗'}</span>
                      <span className="cf-visually-hidden">
                        {kept.has(cap) ? 'kept' : 'dropped'}
                      </span>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </details>
    </div>
  );
}

/** A titled warnings list (import repairs, conversion capability drops). */
export function WarningList({ title, warnings }: { title: string; warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="cf-warnings">
      <h2 className="cf-warnings__title">{title} warnings</h2>
      <ul>
        {warnings.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </div>
  );
}
